// PlaceSearchService.js — birth-place autocomplete.
//
// Uses the Pelias-based geocoder at api-geocode.obrio.net (GET, no key,
// Access-Control-Allow-Origin: * — callable straight from the browser).
// Pelias autocomplete is PREFIX based: "faiz" finds Faizabad, but a misspelt
// "faijabaad" correctly returns nothing — the UI tells the user to keep typing
// / pick from the list instead of silently guessing a location.
//
// The API returns coordinates but no timezone, so a UTC offset is derived from
// the country for the (single-timezone) countries listed below; anything else
// falls back to IST (+5.5) exactly like before, since every existing client is
// India-based. Multi-timezone countries (US, Russia, Canada, Brazil, ...) are
// deliberately not mapped — a wrong guess would be worse than the old default.

const AUTOCOMPLETE_URL = 'https://api-geocode.obrio.net/v1/autocomplete';

// Standard (non-DST) UTC offsets, in hours.
const TZ_BY_COUNTRY = {
  IN: 5.5, NP: 5.75, BD: 6, PK: 5, LK: 5.5, BT: 6, MV: 5, AF: 4.5,
  AE: 4, OM: 4, SA: 3, QA: 3, KW: 3, BH: 3, YE: 3, IQ: 3, JO: 3, LB: 2, IL: 2, TR: 3, IR: 3.5,
  SG: 8, MY: 8, CN: 8, HK: 8, TW: 8, PH: 8, TH: 7, VN: 7, KH: 7, LA: 7, MM: 6.5, JP: 9, KR: 9,
  GB: 0, IE: 0, PT: 0, IS: 0, DE: 1, FR: 1, IT: 1, ES: 1, NL: 1, BE: 1, CH: 1, AT: 1, SE: 1, NO: 1, DK: 1, PL: 1,
  GR: 2, FI: 2, EG: 2, ZA: 2, KE: 3, ET: 3, TZ: 3, NG: 1, GH: 0, NZ: 12,
};

export const timezoneForCountry = (countryCode) => {
  const tz = TZ_BY_COUNTRY[(countryCode || '').toUpperCase()];
  return tz == null ? 5.5 : tz;
};

const fetchOnce = async (q, signal) => {
  const res = await fetch(`${AUTOCOMPLETE_URL}?text=${encodeURIComponent(q)}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!res.ok) throw new Error(`Place search failed (${res.status})`);
  const json = await res.json();
  const seen = new Set();
  return (json.features || [])
    .map((f) => {
      const p = f.properties || {};
      const [lon, lat] = (f.geometry && f.geometry.coordinates) || [];
      if (lat == null || lon == null) return null;
      const label = p.label || p.name;
      if (!label) return null;
      return {
        id: p.gid || `${label}|${lat}|${lon}`,
        label,
        name: p.name || label,
        // second line in the dropdown: the district/county when it adds info
        detail: p.county && p.county !== p.name ? p.county : '',
        countryCode: p.country_code || '',
        latitude: Number(lat),
        longitude: Number(lon),
        timezone: timezoneForCountry(p.country_code),
      };
    })
    .filter((x) => {
      if (!x || seen.has(x.id)) return false;
      seen.add(x.id);
      return true;
    });
};

/**
 * Text → { results, usedQuery }, results being up to 10 candidate places:
 * [{ id, label, name, detail, countryCode, latitude, longitude, timezone }].
 * Pelias autocomplete only matches from the START of a name, so a misspelling
 * ("faijabaad") finds nothing. When the full text has no match, the text is
 * trimmed one letter at a time (down to 3 letters) and the first prefix that
 * does match is returned with usedQuery set to it, so the UI can say "showing
 * matches for 'fai'" instead of a dead end. Rejects with the fetch error; an
 * aborted request rejects with an AbortError the caller should ignore.
 */
export const searchBirthPlaces = async (text, signal) => {
  const q = (text || '').trim();
  if (q.length < 2) return { results: [], usedQuery: q };
  const first = await fetchOnce(q, signal);
  if (first.length) return { results: first, usedQuery: q };
  // No exact prefix match (misspelling): gather matches for progressively
  // shorter prefixes (longest first, de-duplicated) until there are enough.
  const merged = [];
  const seen = new Set();
  let usedQuery = q;
  for (let len = q.length - 1; len >= 3 && len >= q.length - 6 && merged.length < 10; len -= 1) {
    const shorter = q.slice(0, len).trim();
    if (shorter.length < 3) break;
    const part = await fetchOnce(shorter, signal);
    part.forEach((r) => { if (!seen.has(r.id) && merged.length < 10) { seen.add(r.id); merged.push(r); } });
    if (merged.length && usedQuery === q) usedQuery = shorter;
  }
  return { results: merged, usedQuery: merged.length ? usedQuery : q };
};

// What gets stored on a person once a place has been picked.
export const toPlaceGeo = (place) => ({
  label: place.label,
  latitude: place.latitude,
  longitude: place.longitude,
  timezone: place.timezone,
  countryCode: place.countryCode,
});
